<template>
  <!-- HEADER -->
  <div class="header">
    <div class="container-fluid">
      <!-- Table -->
      <div class="card mt-5">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col">
              <!-- Title -->
              <h4 class="card-header-title mb-0">Course Mapping Mitra</h4>
            </div>
            <div class="col-auto">
              <!-- Action Button -->
              <b-button
                class="btn-pill mr-2"
                variant="outline-info"
                size="sm"
                :disabled="isDownloading"
                @click="downloadTemplate()">
                <span
                  v-if="isDownloading"
                  class="spinner-border spinner-border-sm mr-1"
                  role="status">
                </span>
                <span v-else class="fe fe-download"></span>
                Download Template
              </b-button>
              <b-button
                class="btn-pill"
                variant="success"
                size="sm"
                @click="showUploadModal()">
                <span class="fe fe-upload"></span>
                Upload File
              </b-button>
            </div>
          </div>
        </div>
        <!-- Filter -->
        <div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label>Position</label>
                  <v-select
                    v-model="filters.position_id"
                    :options="positionOptions"
                    :reduce="option => option.id"
                    label="name"
                    placeholder="Select Position"
                    @input="onPositionChange">
                  </v-select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>Course</label>
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control"
                    placeholder="Search by course name..."
                    @input="onSearch" />
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>&nbsp;</label>
                  <div>
                    <button
                      type="button"
                      class="btn btn-outline-primary"
                      @click="clearFilters">
                      <span class="fe fe-x"></span> Clear Filter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Message when no filter selected -->
        <div v-if="!showTrainingTable" class="card-body text-center py-5">
          <div class="mb-3">
            <i class="fe fe-filter" style="font-size: 48px; color: #6c757d;"></i>
          </div>
          <h5 class="text-muted">Please select a filter to view trainings</h5>
          <p class="text-muted">Choose Position to load and manage training mappings.</p>
        </div>
        
        <!-- Training Table -->
        <vue-good-table
          v-if="showTrainingTable"
          :line-numbers="true"
          :columns="trainingColumns"
          :rows="trainings"
          :totalRows="trainingsPagination.total"
          :sort-options="{
            enabled: true
          }"
          :pagination-options="{
            enabled: true,
            mode: 'remote',
            position: 'bottom',
            perPage: body.limit,
            perPageDropdown: [10, 50, 100],
            dropdownAllowAll: false,
            nextLabel: 'next',
            prevLabel: 'prev',
            rowsPerPageLabel: 'Rows per page',
            ofLabel: 'of',
            pageLabel: 'page',
            allLabel: 'All',
            setCurrentPage: body.page
          }"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
          style-class="vgt-table border-0 table">
          <!-- Loading -->
          <div slot="emptystate" class="text-center font-size-14">
            {{ isTrainingLoading ? 'Loading trainings...' : (!showTrainingTable ? 'Please select a filter to view trainings' : 'No trainings available') }}
          </div>

          <!-- Custom Rows -->
          <template slot="table-row" slot-scope="props">
            <!-- Training ID -->
            <span v-if="props.column.field == 'training_id'">
              {{ props.row.id }}
            </span>

            <!-- Training Title -->
            <span v-if="props.column.field == 'title'">
              {{ props.row.title }}
            </span>

            <!-- Provider Name -->
            <span v-if="props.column.field == 'provider_name'">
              {{ props.row.provider_name || '-' }}
            </span>

            <!-- Training Method -->
            <span v-if="props.column.field == 'training_method'">
              {{ props.row.training_method || '-' }}
            </span>

            <!-- Hour -->
            <span v-if="props.column.field == 'hour'">
              {{ props.row.hour || '-' }}
            </span>

            <!-- Training Type -->
            <span v-if="props.column.field == 'training_type'">
              <span class="badge badge-info">
                {{ props.row.training_type }}
              </span>
            </span>

            <!-- Required Status with Custom Checkbox -->
            <span v-if="props.column.field == 'required'">
              <div class="form-check">
                <input
                  type="checkbox"
                  :checked="isTrainingSelected(props.row.id)"
                  @change="toggleTrainingSelection(props.row.id)"
                  class="form-check-input"
                  :id="'training_' + props.row.id">
              </div>
            </span>
          </template>
        </vue-good-table>

        <!-- Save Button -->
        <div v-if="showTrainingTable" class="card-footer">
          <div class="row">
            <div class="col-12 text-right">
              <button 
                type="button" 
                class="btn btn-primary"
                @click="saveAdd"
                :disabled="isSaving || selectedTrainings.length === 0">
                <span 
                  v-if="isSaving" 
                  class="spinner-border spinner-border-sm me-2" 
                  role="status">
                </span>
                Save Training Mapping Mitra
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal -->
      <b-modal 
        id="modal-course-mapping-mitra" 
        centered 
        hide-footer 
        :title="modalTitle"
        size="lg">
        <!-- Add Form -->
        <form @submit.prevent="saveAdd()" v-if="isAdd === true">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Position <span class="text-danger">*</span></label>
                <v-select
                  v-validate="'required'"
                  name="Position"
                  v-model="bodyAdd.position_id"
                  :options="positionOptions"
                  :reduce="option => option.position_id"
                  label="position_title"
                  placeholder="Select Position">
                </v-select>
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Position')"
                  >{{ errors.first("Position") }}</span>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label>Competency <span class="text-danger">*</span></label>
                <v-select
                  v-validate="'required'"
                  name="Competency"
                  v-model="bodyAdd.competency_id"
                  :options="competencyOptions"
                  :reduce="option => option.id"
                  label="name"
                  placeholder="Select Competency">
                </v-select>
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Competency')"
                  >{{ errors.first("Competency") }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Role ID <span class="text-danger">*</span></label>
                <v-select
                  v-validate="'required'"
                  name="Role ID"
                  v-model="bodyAdd.role_id"
                  :options="[
                    { value: 'PO', label: 'PO - Pengawas Operasional' },
                    { value: 'PT', label: 'PT - Pengawas Teknis' },
                    { value: 'TT', label: 'TT - Tenaga Teknis' }
                  ]"
                  :reduce="option => option.value"
                  label="label"
                  placeholder="Select Role ID">
                </v-select>
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Role ID')"
                  >{{ errors.first("Role ID") }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Required</label>
                <div class="form-check">
                  <input
                    type="checkbox"
                    v-model="bodyAdd.required"
                    class="form-check-input"
                    id="requiredAdd">
                  <label class="form-check-label" for="requiredAdd">
                    This competency is required for this position
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-course-mapping-mitra')">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              :disabled="isLoading">
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              Save
            </button>
          </div>
        </form>

        <!-- Edit Form -->
        <form @submit.prevent="saveUpdate()" v-else-if="isAdd === false">
          <div v-if="isLoading && !bodyUpdate.id" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading course mapping mitra details...</p>
          </div>
          <div v-else>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label>Position <span class="text-danger">*</span></label>
                  <v-select
                    v-validate="'required'"
                    name="Position"
                    v-model="bodyUpdate.position_id"
                    :options="positionOptions"
                    :reduce="option => option.position_id"
                    label="position_title"
                    placeholder="Select Position">
                  </v-select>
                  <span
                    class="text-sm text-danger"
                    v-show="errors.has('Position')"
                    >{{ errors.first("Position") }}</span>
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group">
                  <label>Competency <span class="text-danger">*</span></label>
                  <v-select
                    v-validate="'required'"
                    name="Competency"
                    v-model="bodyUpdate.competency_id"
                    :options="competencyOptions"
                    :reduce="option => option.id"
                    label="name"
                    placeholder="Select Competency">
                  </v-select>
                  <span
                    class="text-sm text-danger"
                    v-show="errors.has('Competency')"
                    >{{ errors.first("Competency") }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Role ID <span class="text-danger">*</span></label>
                  <v-select
                    v-validate="'required'"
                    name="Role ID"
                    v-model="bodyUpdate.role_id"
                    :options="[
                      { value: 'PO', label: 'PO - Pengawas Operasional' },
                      { value: 'PT', label: 'PT - Pengawas Teknis' },
                      { value: 'TT', label: 'TT - Tenaga Teknis' }
                    ]"
                    :reduce="option => option.value"
                    label="label"
                    placeholder="Select Role ID">
                  </v-select>
                  <span
                    class="text-sm text-danger"
                    v-show="errors.has('Role ID')"
                    >{{ errors.first("Role ID") }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Required</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      v-model="bodyUpdate.required"
                      class="form-check-input"
                      id="requiredUpdate">
                    <label class="form-check-label" for="requiredUpdate">
                      This competency is required for this position
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group text-right">
              <button
                type="button"
                class="btn btn-secondary me-2"
                @click="$bvModal.hide('modal-course-mapping-mitra')">
                Cancel
              </button>
              <button
                class="btn btn-danger"
                type="submit"
                :disabled="isLoading">
                <span
                  v-if="isLoading"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"></span>
                Update
              </button>
            </div>
          </div>
        </form>

        <!-- View Mode -->
        <div v-else>
          <div v-if="isLoading" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading course mapping mitra details...</p>
          </div>
          <div v-else>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>ID:</strong></label>
                  <p>{{ viewDetailData.id }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Position:</strong></label>
                  <p>{{ viewDetailData.position_title || getPositionName(viewDetailData.position_id) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Competency:</strong></label>
                  <p>{{ viewDetailData.competency_name || getCompetencyName(viewDetailData.competency_id) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Role ID:</strong></label>
                  <p>
                    <span 
                      class="badge" 
                      :class="{
                        'badge-success': viewDetailData.role_id === 'PO',
                        'badge-warning': viewDetailData.role_id === 'PT', 
                        'badge-info': viewDetailData.role_id === 'TT'
                      }">
                      {{ viewDetailData.role_id }}
                    </span>
                    {{ getRoleDescription(viewDetailData.role_id) }}
                  </p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Required:</strong></label>
                  <p>
                    <span 
                      class="badge" 
                      :class="viewDetailData.required ? 'badge-danger' : 'badge-secondary'">
                      {{ viewDetailData.required ? 'Required' : 'Optional' }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary"
              @click="$bvModal.hide('modal-course-mapping-mitra')">
              Close
            </button>
          </div>
        </div>
      </b-modal>

      <!-- Upload Modal -->
      <b-modal 
        id="modal-upload-mitra" 
        centered 
        hide-footer 
        title="Upload Training Mapping Mitra File"
        size="lg">
        <form @submit.prevent="uploadFile()">
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label>Select File <span class="text-danger">*</span></label>
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept=".xlsx"
                  @change="onFileChange"
                  required />
                <small class="text-muted">Accepted format: .xlsx (max 5MB)</small>
              </div>
            </div>
            <div class="col-12" v-if="selectedFile">
              <div class="form-group">
                <label>File Preview:</label>
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <i class="fe fe-file-text text-primary me-2" style="font-size: 2rem;"></i>
                      <div>
                        <p class="mb-0 font-weight-bold">{{ selectedFile.name }}</p>
                        <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-upload-mitra')">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="!selectedFile || isUploading">
              <span
                v-if="isUploading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ isUploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import logic from './course-mapping-mitra.logic.js';

export default {
  ...logic
}
</script>

<style src="./course-mapping-mitra.css" scoped></style>
